package fr.manyverse.app.infrastructure.image

import coil3.ImageLoader
import coil3.PlatformContext
import coil3.disk.DiskCache
import coil3.memory.MemoryCache
import coil3.network.ktor3.KtorNetworkFetcherFactory
import coil3.request.crossfade
import okio.Path.Companion.toPath

/**
 * Creates a configured ImageLoader with disk and memory caching.
 *
 * @param context The platform-specific context
 * @param cacheDirectory The directory path for disk cache storage
 * @return Configured ImageLoader instance
 */
fun createImageLoader(context: PlatformContext, cacheDirectory: String): ImageLoader {
    return ImageLoader.Builder(context)
        .components {
            add(KtorNetworkFetcherFactory())
        }
        .memoryCache {
            MemoryCache.Builder()
                // Cache up to 25% of application memory
                .maxSizePercent(context, 0.25)
                .build()
        }
        .diskCache {
            newDiskCache(cacheDirectory)
        }
        .crossfade(true)
        .build()
}

/**
 * Creates a disk cache for storing downloaded images.
 * Only creates the cache if a valid directory path is provided.
 *
 * @param cacheDirectory The directory path for disk cache storage
 * @return DiskCache instance, or null if directory is empty
 */
private fun newDiskCache(cacheDirectory: String): DiskCache? {
    if (cacheDirectory.isEmpty()) {
        return null
    }

    return DiskCache.Builder()
        .directory(cacheDirectory.toPath())
        .maxSizeBytes(100L * 1024 * 1024) // 100 MB
        .build()
}
